import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../services/api';
import styles from './Login.module.css';

export default function Login() {
  const { loginWithToken, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Email OTP is the ONLY staff sign-in method. Password auth was removed
  // along with the default-credentials hint that used to be rendered below the
  // password form; POST /api/auth/login now returns 403 (see routes/auth.js).
  // OTP step: 'request' or 'verify'
  const [otpStep, setOtpStep] = useState('request');
  const [maskedEmail, setMaskedEmail] = useState('');

  // Redirect if already authenticated
  if (isAuthenticated && !loading) {
    return <Navigate to="/" replace />;
  }

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const result = await auth.requestAdminOTP(email);
      setMaskedEmail(result.email || email);
      setOtpStep('verify');
      setSuccess('Verification code sent! Check your email.');
      
      // If dev OTP is returned, show it
      if (result._devOtp) {
        setSuccess(`Verification code sent! (Dev mode: ${result._devOtp})`);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send verification code';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await auth.verifyAdminOTP(email, otpCode);
      
      if (result.success && result.token) {
        // Use the token to log in
        loginWithToken(result.token, result.user);
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Verification failed';
      setError(message);
      
      // If max attempts or expired, go back to request step
      if (err.response?.data?.reason === 'max_attempts' || err.response?.data?.reason === 'expired') {
        setOtpStep('request');
        setOtpCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToRequest = () => {
    setOtpStep('request');
    setOtpCode('');
    setError('');
    setSuccess('');
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard} role="main" aria-label="Admin login">
        <div className={styles.logoSection}>
          <div className={styles.logoIcon} aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1>Lab Environment</h1>
          <p>Admin Dashboard</p>
        </div>

        {/* Sign-in is email-OTP only. The password tab and the default-credentials
            hint were removed: the API no longer accepts password authentication. */}

        {/* OTP Login Form - Request Step */}
        {otpStep === 'request' && (
          <form onSubmit={handleRequestOTP} className={styles.loginForm}>
            {error && <div className={styles.error} role="alert">{error}</div>}
            {success && <div className={styles.success} role="status">{success}</div>}

            <div className={styles.formGroup}>
              <label htmlFor="otp-email">Email Address</label>
              <input
                id="otp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your.email@udst.edu.qa"
                autoComplete="email"
                disabled={isLoading}
              />
            </div>

            <button 
              type="submit" 
              className={styles.loginButton}
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  Sending...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  Send Login Code
                </>
              )}
            </button>

            <div className={styles.otpHint}>
              <p>Enter your registered email to receive a one-time login code. No password required!</p>
            </div>
          </form>
        )}

        {/* OTP Login Form - Verify Step */}
        {otpStep === 'verify' && (
          <form onSubmit={handleVerifyOTP} className={styles.loginForm}>
            {error && <div className={styles.error} role="alert">{error}</div>}
            {success && <div className={styles.success} role="status">{success}</div>}

            <div className={styles.otpInfo}>
              <div className={styles.otpEmailIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
                  <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                </svg>
              </div>
              <p>Code sent to</p>
              <strong>{maskedEmail}</strong>
              <button 
                type="button" 
                className={styles.changeEmail}
                onClick={goBackToRequest}
                disabled={isLoading}
              >
                Change email
              </button>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="otp-code">Verification Code</label>
              <input
                id="otp-code"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder="000000"
                autoComplete="one-time-code"
                disabled={isLoading}
                maxLength={6}
                className={styles.otpInput}
              />
            </div>

            <button 
              type="submit" 
              className={styles.loginButton}
              disabled={isLoading || otpCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  Verifying...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Verify & Log In
                </>
              )}
            </button>

            <button 
              type="button" 
              className={styles.resendButton}
              onClick={handleRequestOTP}
              disabled={isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="1 4 1 10 7 10"/>
                <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
              </svg>
              Resend Code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
